import { useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SoftCard } from '@/components/ui/soft-card';
import { useToast } from '@/components/ui/toast';
import { useChannelActions } from '@/hooks/useChannelActions';

const TWIN_CHANNEL_TYPES = ['product-reservations', 'local-checkout'];

// Coerce legacy/odd shapes (a bare string was used pre-array on a small number
// of old records) into a string[] before reading the first entry — otherwise
// a stringified phone number renders as just its first character.
const toPhoneArray = (value) => {
	if (Array.isArray(value)) return value.filter((entry) => typeof entry === 'string' && entry.trim().length > 0);
	if (typeof value === 'string' && value.trim()) return [value.trim()];
	return [];
};

/**
 * v1 "SMS only for shops that already have it" gate (NEA-6015): we surface
 * the SMS controls only if the local-checkout record already shows evidence
 * of SMS being configured — either the toggle is on, or a phone number is
 * saved. Greenfield shops don't see anything until the charging policy
 * decision is taken and we can offer SMS to everyone.
 */
export const hasExistingSmsConfig = (localCheckoutChannel) => {
	const config = localCheckoutChannel?.config;
	if (!config) return false;
	if (config.smsEnabled === true) return true;
	return toPhoneArray(config.notificationPhoneNumbers).length > 0;
};

/**
 * SMS notification controls for Local Checkout — phone number + on/off toggle.
 * Writes both fields to BOTH twin channels so the orders pipeline (which
 * reads `OrderOptions.allNotificationPhoneNumbers` across both records)
 * stays consistent regardless of which channel a given order came in on.
 *
 * Only rendered when the shop already has SMS configured (see
 * `hasExistingSmsConfig`) — v1 doesn't allow new shops to opt in because the
 * billing policy for SMS is still open.
 */
export function SmsNotificationsCard({ shopId, channels, phoneNumbers, smsEnabled }) {
	const { update } = useChannelActions(shopId);
	const { toast } = useToast();
	const normalisedPhones = toPhoneArray(phoneNumbers);
	const existingPhone = normalisedPhones[0] || '';
	const [localPhone, setLocalPhone] = useState(existingPhone);

	const writeBoth = async (config) => {
		const writes = TWIN_CHANNEL_TYPES.filter((type) => channels.some((c) => c.type === type)).map((type) => update(type, { config }));
		await Promise.all(writes);
	};

	const handlePhoneBlur = async () => {
		const next = localPhone.trim();
		if (next === existingPhone) return;
		try {
			await writeBoth({ notificationPhoneNumbers: next ? [next] : [] });
		} catch (err) {
			toast({
				title: 'Could not save phone number',
				description: err?.message ?? 'Please try again.',
				variant: 'destructive'
			});
		}
	};

	const handleSmsToggle = async (event) => {
		const checked = event.target.checked;
		try {
			await writeBoth({ smsEnabled: checked });
		} catch (err) {
			toast({
				title: 'Could not update SMS notifications',
				description: err?.message ?? 'Please try again.',
				variant: 'destructive'
			});
		}
	};

	return (
		<SoftCard className="space-y-5 !p-5 md:!p-6">
			<div>
				<h3 className="text-sm font-bold text-foreground">SMS notifications</h3>
				<p className="mt-1 text-xs text-muted-foreground">
					We can text you whenever a customer places a new order, so you don't have to keep an inbox open. Used by Local Checkout
					and Product Reservations alike.
				</p>
			</div>

			<div className="space-y-2">
				<Label htmlFor="notification-phone" className="text-xs font-semibold text-foreground">
					Notification phone number
				</Label>
				<Input
					id="notification-phone"
					type="tel"
					autoComplete="tel"
					placeholder="+44 7700 900123"
					value={localPhone}
					onChange={(event) => setLocalPhone(event.target.value)}
					onBlur={handlePhoneBlur}
				/>
				<p className="text-xs text-muted-foreground">Include the country dialling code, e.g. +44.</p>
			</div>

			<Checkbox
				label="Send text messages for new orders"
				checked={Boolean(smsEnabled)}
				onChange={handleSmsToggle}
				data-testid="sms-enabled-toggle"
			/>
		</SoftCard>
	);
}
